import React, { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
	fetchOrderBySession,
	fetchReportHtml,
	uploadReportPdf,
} from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { useOrderStatus } from "../hooks/useOrderStatus";
import { SUBSCRIPTION_PLANS } from "../const/subscriptionPlan";
import { isCreditTopUpPlan } from "../const/creditTopUpPacks";

const ReportGeneration = () => {
	const { orderId: paramOrderId } = useParams();
	const [searchParams] = useSearchParams();
	const sessionId = searchParams.get("session_id"); // https://heliocentric-astrology-application.vercel.app/report-generation?session_id=cs_test_a1Y7cpGOcYYDNog7yBo7ZEMcA11HMPT0aMw5JLlLqPd0TQIXyfWOnx0abR
	const navigate = useNavigate();
	const { user } = useAuth();

	const [orderId, setOrderId] = useState(paramOrderId);
	const [error, setError] = useState(null);
	const [reportHtml, setReportHtml] = useState(null);
	const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
	const [pdfGenerated, setPdfGenerated] = useState(false);
	const [uploadedReportUrl, setUploadedReportUrl] = useState(null);
	const renderFrameRef = useRef(null);

	// Use the order status hook for polling
	const { status, progress, reportUrl, orderInfo, error: pollingError, orderError } = useOrderStatus(orderId, {
		pollingInterval: 5000,
		enabled: !!orderId,
	});

	// Use uploaded report URL if available, otherwise use the one from the hook
	const finalReportUrl = uploadedReportUrl || reportUrl;

	// Fetch Order ID from sessionId
	useEffect(() => {
		const fetchOrderId = async () => {
			if (!orderId && sessionId) {
				try {
					const data = await fetchOrderBySession(sessionId);
					if (data.success) {
						if (data.isTopUp || isCreditTopUpPlan(data.planType)) {
							navigate("/subscription-success?type=topup", { replace: true });
							return;
						}
						if (data.planType === SUBSCRIPTION_PLANS.PREMIUM) {
							navigate("/subscription-success", { replace: true });
							return;
						}
						setOrderId(data.orderId);
					} else {
						setError("Order not found.");
					}
				} catch (err) {
					console.error(err);
					setError("Failed to retrieve order details.");
				}
			} else if (!orderId && !sessionId) {
				setError("No order information provided.");
			}
		};
		fetchOrderId();
	}, [sessionId, orderId, navigate]);

	// Order status polling is now handled by useOrderStatus hook

	if (error) {
		return (
			<div className='min-h-screen flex items-center justify-center bg-gray-900 text-white'>
				<div className='text-center p-8 bg-gray-800 rounded-xl shadow-2xl border border-red-500/30'>
					<h2 className='text-2xl font-bold text-red-400 mb-4'>Error</h2>
					<p>{error}</p>
					<button
						onClick={() => navigate("/")}
						className='mt-6 px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-full transition-colors'
					>
						Return Home
					</button>
				</div>
			</div>
		);
	}

	const handleGeneratePdf = async () => {
		if (!orderId) return;
		setError(null);
		setIsGeneratingPdf(true);

		try {
			// 1. Ensure we have the backend-prepared HTML
			let html = reportHtml;
			if (!html) {
				const data = await fetchReportHtml(orderId);
				if (!data.success) {
					throw new Error(
						data.message || "Report HTML is not ready yet. Please try again shortly.",
					);
				}
				html = data.html;
				setReportHtml(html);
			}

			// 2. Render the HTML into an offscreen iframe to preserve styles
			let iframe = renderFrameRef.current;
			if (!iframe) {
				iframe = document.createElement("iframe");
				iframe.style.position = "fixed";
				iframe.style.top = "-10000px";
				iframe.style.left = "-10000px";
				iframe.style.width = "900px";
				iframe.style.height = "1400px";
				iframe.style.visibility = "hidden";
				document.body.appendChild(iframe);
				renderFrameRef.current = iframe;
			}

			await new Promise((resolve, reject) => {
				iframe.onload = () => resolve();
				iframe.onerror = () =>
					reject(new Error("Failed to load report HTML into iframe."));
				iframe.srcdoc = html;
			});

			const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
			const element = iframeDoc.body;

			// 3. Use html2canvas + jsPDF to generate the PDF blob
			const html2canvas = window.html2canvas;
			if (typeof html2canvas !== "function") {
				throw new Error("html2canvas is not available on the window object.");
			}

			const { jsPDF } = await import("jspdf");

			const canvas = await html2canvas(element, {
				scale: 2,
				useCORS: true,
				scrollY: -window.scrollY,
			});

			const imgData = canvas.toDataURL("image/jpeg", 0.9);
			const pdf = new jsPDF("p", "pt", "a4");
			const pageWidth = pdf.internal.pageSize.getWidth();
			const pageHeight = pdf.internal.pageSize.getHeight();

			// Margins
			const margin = 20; // Side margin
			const marginX = margin;
			const marginTop = 50; // Increased top margin
			const marginBottom = 40; // Bottom margin (increased)

			const contentWidth = pageWidth - (2 * marginX);
			const contentHeight = pageHeight - marginTop - marginBottom;

			const imgWidth = contentWidth;
			const imgHeight = (canvas.height * imgWidth) / canvas.width;

			let heightLeft = imgHeight;
			let position = 0;
			let pageNumber = 1;
			const totalPages = Math.ceil(imgHeight / contentHeight);

			// First Page
			pdf.addImage(imgData, "JPEG", marginX, marginTop, imgWidth, imgHeight);

			// Mask Top Margin (for first page if needed, or consistent header)
			pdf.setFillColor(255, 255, 255);
			pdf.rect(0, 0, pageWidth, marginTop, 'F');

			// Mask Bottom Margin (white rect)
			pdf.setFillColor(255, 255, 255);
			pdf.rect(0, pageHeight - marginBottom, pageWidth, marginBottom, 'F');

			// Page Number
			pdf.setFontSize(10);
			pdf.setTextColor(50, 50, 50);
			pdf.text(`Page ${pageNumber} of ${totalPages}`, pageWidth - marginX, pageHeight - 20, { align: 'right' });

			heightLeft -= contentHeight;

			while (heightLeft > 0) {
				position -= contentHeight;
				pdf.addPage();
				pageNumber++;

				// Add Image shifted up
				pdf.addImage(imgData, "JPEG", marginX, marginTop + position, imgWidth, imgHeight);

				// Mask Top Margin
				pdf.setFillColor(255, 255, 255);
				pdf.rect(0, 0, pageWidth, marginTop, 'F');

				// Mask Bottom Margin
				pdf.setFillColor(255, 255, 255);
				pdf.rect(0, pageHeight - marginBottom, pageWidth, marginBottom, 'F');

				// Page Number
				pdf.setFontSize(10);
				pdf.setTextColor(100, 100, 100);
				pdf.text(`Page ${pageNumber} of ${totalPages}`, pageWidth - marginX, pageHeight - marginBottom + 12, { align: 'right' });

				heightLeft -= contentHeight;
			}

			const pdfBlob = pdf.output("blob");

			// 4. Upload to backend -> Cloudinary -> email
			try {
				const uploadResult = await uploadReportPdf(orderId, pdfBlob);
				if (uploadResult?.success && uploadResult.reportUrl) {
					setUploadedReportUrl(uploadResult.reportUrl);
				}
			} catch (uploadError) {
				console.error("Failed to upload PDF to backend:", uploadError);
				// Non-fatal: user still gets the download
			}

			// 5. Auto-download for the user with meaningful filename
			const userName = (orderInfo.preparingFor || 'User').replace(/[^a-zA-Z0-9]/g, '_');
			const reportType = (orderInfo.planType || 'Report').replace(/[^a-zA-Z0-9_-]/g, '_');
			const dateStr = new Date().toISOString().split('T')[0];
			const filename = `${userName}_${reportType}_${dateStr}.pdf`;
			pdf.save(filename);

			setPdfGenerated(true);
		} catch (err) {
			console.error("PDF generation failed:", err);
			setError(
				err.message ||
				"Failed to generate PDF. Please refresh the page and try again.",
			);
		} finally {
			setIsGeneratingPdf(false);
		}
	};

	if (status === "completed" && finalReportUrl) {
		return (
			<div className='min-h-screen flex items-center justify-center bg-gray-900 text-white p-4'>
				<motion.div
					initial={{ opacity: 0, scale: 0.9 }}
					animate={{ opacity: 1, scale: 1 }}
					className='max-w-md w-full bg-gray-800 rounded-2xl shadow-2xl p-8 border border-purple-500/30 text-center'
				>
					<div className='w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6'>
						<svg
							className='w-10 h-10 text-green-400'
							fill='none'
							stroke='currentColor'
							viewBox='0 0 24 24'
						>
							<path
								strokeLinecap='round'
								strokeLinejoin='round'
								strokeWidth={2}
								d='M5 13l4 4L19 7'
							/>
						</svg>
					</div>
					<h2 className='text-3xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent'>
						Report Ready!
					</h2>
					<p className='text-gray-300 mb-8'>
						Your Heliocentric Astrology report has been successfully generated.
					</p>
					<a
						href={finalReportUrl}
						target='_blank'
						rel='noopener noreferrer'
						className='block w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl font-bold text-lg hover:opacity-90 transition-opacity shadow-lg shadow-purple-500/25'
					>
						Download Report PDF
					</a>
					<button
						onClick={() => navigate("/")}
						className='mt-4 text-gray-400 hover:text-white text-sm'
					>
						Return to Home
					</button>
				</motion.div>
			</div>
		);
	}

	if (status === "failed") {
		// Prioritize order-specific error, then progress message, then polling error, then default
		const errorMessage = orderError || progress?.message || pollingError || "We encountered an issue while generating your report. Please try again later or contact support.";

		return (
			<div className='min-h-screen flex items-center justify-center bg-gray-900 text-white p-4'>
				<motion.div
					initial={{ opacity: 0, scale: 0.9 }}
					animate={{ opacity: 1, scale: 1 }}
					className='max-w-md w-full bg-gray-800 rounded-2xl shadow-2xl p-8 border border-red-500/30 text-center'
				>
					<div className='w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6'>
						<svg
							className='w-10 h-10 text-red-400'
							fill='none'
							stroke='currentColor'
							viewBox='0 0 24 24'
						>
							<path
								strokeLinecap='round'
								strokeLinejoin='round'
								strokeWidth={2}
								d='M6 18L18 6M6 6l12 12'
							/>
						</svg>
					</div>
					<h2 className='text-3xl font-bold mb-4 bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent'>
						Generation Failed
					</h2>
					<p className='text-gray-300 mb-4'>
						{errorMessage}
					</p>
					<div className='flex gap-3 mt-8'>
						<button
							onClick={() => navigate("/plans")}
							className='flex-1 py-3 px-4 bg-indigo-600 hover:bg-indigo-700 rounded-xl font-semibold text-sm transition-colors'
						>
							View Plans
						</button>
						<button
							onClick={() => navigate("/")}
							className='flex-1 py-3 px-4 bg-gray-700 hover:bg-gray-600 rounded-xl font-semibold text-sm transition-colors'
						>
							Return Home
						</button>
					</div>
				</motion.div>
			</div>
		);
	}

	return (
		<div className='min-h-screen flex items-center justify-center bg-gray-900 text-white p-4'>
			<div className='max-w-md w-full'>
				<div className='text-center mb-10'>
					<h1 className='text-3xl font-bold mb-2'>Generating Your Report</h1>
					<p className='text-gray-400'>Please wait while the cosmos aligns...</p>
				</div>

				<div className='bg-gray-800 rounded-2xl p-8 shadow-2xl border border-purple-500/20'>
					<div className='mb-6 flex justify-between items-end'>
						<span className='text-lg font-medium text-purple-300 capitalize'>
							{progress.message || "Processing..."}
						</span>
						<span className='text-2xl font-bold text-white'>
							{progress.percentage}%
						</span>
					</div>

					<div className='h-5 bg-gray-700/50 rounded-full overflow-hidden mb-8 relative backdrop-blur-sm border border-gray-600/30'>
						{/* Background glow */}
						<motion.div
							className='absolute -inset-1 rounded-full blur-md'
							style={{
								background: `linear-gradient(90deg, rgba(168, 85, 247, 0.3), rgba(236, 72, 153, 0.3))`,
								width: `${progress.percentage + 2}%`,
							}}
							animate={{
								opacity: [0.4, 0.7, 0.4],
							}}
							transition={{
								duration: 1.5,
								repeat: Infinity,
								ease: 'easeInOut',
							}}
						/>

						{/* Main progress bar */}
						<motion.div
							className='h-full rounded-full relative overflow-hidden'
							style={{
								background: 'linear-gradient(90deg, #8B5CF6, #EC4899, #8B5CF6)',
								backgroundSize: '200% 100%',
							}}
							initial={{ width: 0 }}
							animate={{
								width: `${progress.percentage}%`,
								backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
							}}
							transition={{
								width: { duration: 0.5, ease: 'easeOut' },
								backgroundPosition: { duration: 3, repeat: Infinity, ease: 'linear' },
							}}
						>
							{/* Shimmer wave */}
							<motion.div
								className='absolute inset-0'
								style={{
									background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
									width: '50%',
								}}
								animate={{ x: ['-100%', '300%'] }}
								transition={{
									duration: 1.2,
									repeat: Infinity,
									ease: 'easeInOut',
									repeatDelay: 0.3,
								}}
							/>

							{/* Glowing edge */}
							<motion.div
								className='absolute right-0 top-0 bottom-0 w-8'
								style={{
									background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.6))',
								}}
								animate={{
									opacity: [0.5, 1, 0.5],
								}}
								transition={{
									duration: 0.8,
									repeat: Infinity,
									ease: 'easeInOut',
								}}
							/>
						</motion.div>
					</div>

					<div className='space-y-4'>
						{["calculating", "interpreting", "generating_pdf", "uploading"].map(
							(step, index) => {
								const steps = [
									"calculating",
									"interpreting",
									"generating_pdf",
									"uploading",
								];
								const currentStepIndex = steps.indexOf(progress.step);
								const stepIndex = steps.indexOf(step);

								let statusColor = "text-gray-600";
								let icon = (
									<div className='w-6 h-6 rounded-full border-2 border-gray-600 flex items-center justify-center text-xs'>
										{index + 1}
									</div>
								);

								if (stepIndex < currentStepIndex || progress.step === "completed") {
									statusColor = "text-green-400";
									icon = (
										<div className='w-6 h-6 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center'>
											<svg
												className='w-3 h-3 text-green-500'
												fill='none'
												stroke='currentColor'
												viewBox='0 0 24 24'
											>
												<path
													strokeLinecap='round'
													strokeLinejoin='round'
													strokeWidth={3}
													d='M5 13l4 4L19 7'
												/>
											</svg>
										</div>
									);
								} else if (stepIndex === currentStepIndex) {
									statusColor = "text-purple-400";
									icon = (
										<div className='w-6 h-6 rounded-full border-2 border-purple-500 flex items-center justify-center animate-pulse'>
											<div className='w-2 h-2 bg-purple-500 rounded-full' />
										</div>
									);
								}

								return (
									<div
										key={step}
										className={`flex items-center space-x-3 ${statusColor}`}
									>
										{icon}
										<span className='capitalize font-medium'>
											{step.replace("_", " ")}
										</span>
									</div>
								);
							},
						)}
					</div>

					{status === "completed" && !finalReportUrl && (
						<div className='mt-8 space-y-3'>
							<p className='text-sm text-gray-300'>
								Your report content is ready. Click generate to produce the PDF,
								upload it, and auto-download it to your device.
							</p>
							<button
								onClick={handleGeneratePdf}
								disabled={isGeneratingPdf}
								className='w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed'
							>
								{isGeneratingPdf ? "Generating PDF..." : "Generate PDF Now"}
							</button>
							{pdfGenerated && (
								<p className='text-xs text-green-400'>
									PDF generated on your device. If email delivery was enabled,
									you&apos;ll receive a copy shortly.
								</p>
							)}
						</div>
					)}

					{error && (
						<p className='mt-4 text-sm text-red-400 text-center'>{error}</p>
					)}
				</div>
			</div>
		</div>
	);
};

export default ReportGeneration;



/**
 * our case
 * http://localhost:5000/api/payment/status/session_id=69316d5561e445fce0ca6e40
 * url
 * http://localhost:3000/report-generation/session_id=69316d5561e445fce0ca6e40
 */


/**
 * working case
 * https://heliocentric-astrology-application-a78b.onrender.com/api/payment/status/69316de5dc4ba570529bcdeb
 * url
 * https://heliocentric-astrology-application.vercel.app/report-generation?session_id=cs_test_a1Y7cpGOcYYDNog7yBo7ZEMcA11HMPT0aMw5JLlLqPd0TQIXyfWOnx0abR
 */